import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Login.css';

function Login() {
  const { register, verifyEmail, login, adminLogin } = useAuth();
  const [mode, setMode] = useState('login'); // 'login', 'register', 'verify', 'admin'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [username, setUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const result = await register(email, password, name);
      if (result.requiresVerification) {
        setMessage(result.message);
        setMode('verify');
      }
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await verifyEmail(email, code);
    } catch (err) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setMessage('');
    setLoading(true);

    try {
      await api.resendCode(email);
      setMessage('Verification code sent!');
    } catch (err) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
    } catch (err) {
      if (err.requiresVerification) {
        setMessage('Please verify your email. A new code has been sent.');
        setMode('verify');
      } else {
        setError(err.message || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await adminLogin(username, adminPassword);
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <img src="/logo.png" alt="Logo" className="login-logo" />
          <h1>La Lengua</h1>
          <p>98th Academy Awards Predictions</p>
        </div>

        <div className="login-card">
          {error && <div className="error-message">{error}</div>}
          {message && <div className="success-message">{message}</div>}

          {mode === 'login' && (
            <>
              <form onSubmit={handleLogin}>
                <div className="form-group">
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>

              <div className="login-divider">
                <span>or</span>
              </div>

              <button
                className="btn btn-secondary btn-full"
                onClick={() => { setMode('register'); setError(''); setMessage(''); }}
              >
                Create Account
              </button>

              <button
                className="btn-link"
                onClick={() => { setMode('admin'); setError(''); setMessage(''); }}
              >
                Admin Login
              </button>
            </>
          )}

          {mode === 'register' && (
            <>
              <h3>Create Account</h3>
              <form onSubmit={handleRegister}>
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="password"
                    placeholder="Password (min 6 characters)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={6}
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Account'}
                </button>
              </form>

              <button
                className="btn-link"
                onClick={() => { setMode('login'); setError(''); setMessage(''); }}
              >
                Already have an account? Sign in
              </button>
            </>
          )}

          {mode === 'verify' && (
            <>
              <h3>Verify Your Email</h3>
              <p className="verify-info">Enter the 6-digit code sent to {email}</p>
              <form onSubmit={handleVerify}>
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    className="code-input"
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-full" disabled={loading || code.length !== 6}>
                  {loading ? 'Verifying...' : 'Verify'}
                </button>
              </form>

              <button
                className="btn-link"
                onClick={handleResendCode}
                disabled={loading}
              >
                Resend Code
              </button>

              <button
                className="btn-link"
                onClick={() => { setMode('login'); setError(''); setMessage(''); }}
              >
                Back to Login
              </button>
            </>
          )}

          {mode === 'admin' && (
            <>
              <h3>Admin Login</h3>
              <form onSubmit={handleAdminLogin}>
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="password"
                    placeholder="Password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>

              <button
                className="btn-link"
                onClick={() => { setMode('login'); setError(''); setMessage(''); }}
              >
                Back to User Login
              </button>
            </>
          )}
        </div>

        <div className="login-footer">
          <div className="legal-links">
            <Link to="/terms">Terms of Service</Link>
            <span>|</span>
            <Link to="/privacy">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
